import type { ForwardedRef, ReactNode } from 'react'
import {
  type Dispatch,
  type SetStateAction,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import type {
  ColumnDef,
  Header,
  InitialTableState,
  OnChangeFn,
  PaginationState,
  SortingState,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'

import ChevronBottomIcon from '@/assets/icons/icon-chevron-bottom.svg'
import { EmptySlot } from '@/components/ui/empty/empty-provider.tsx'
import { EmptyTableContent } from '@/components/ui/empty/empty-table-content.tsx'
import { classNames } from '@/utils/helpers'

import type { ExportProps } from '../export.tsx'
import { Export } from '../export.tsx'
import { SearchInput } from '../search-input.tsx'
import { Skeleton } from '../skeleton.tsx'
import { DataTablePagination } from './data-table-pagination.tsx'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table.tsx'

type DataTableProps<TData, TValue = unknown> = {
  extra?: ReactNode
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  initialState?: InitialTableState
  title?: string
  titleClassName?: string
  enableSearch?: boolean
  searchColumn?: string
  searchQuery?: string
  enablePagination?: boolean
  enableSorting?: boolean
  paginationState?: PaginationState
  manualSorting?: boolean
  onPaginationChange?: Dispatch<SetStateAction<PaginationState>>
  sortingState?: SortingState
  onSortingChange?: OnChangeFn<SortingState>
  className?: string
  cellClassName?: string
  hasActionColumn?: boolean | undefined
  enableSortingRemoval?: boolean
  hideHeader?: boolean
  wrapperClassName?: string
  isLoading?: boolean
  rowId?: (row: TData) => string | number
  defaultColumn?: Partial<ColumnDef<TData, TValue>>
  testId?: string
} & (
  | { enableExport?: undefined; exportOptions?: undefined }
  | {
      enableExport: true
      exportOptions: ExportProps
    }
)

/**
 * A table wrapper component that displays complex tabular data and handles pagination.
 *
 * @param data - The data to be displayed in the table.
 * @param columns - The columns configuration for the table.
 * @param hasActionColumn - Boolean flag indicating if the table has the "Actions" column.
 *
 * @returns - The rendered DataTable component.
 */
const DataTableInner = <TData, TValue>(
  {
    columns,
    data,
    manualSorting,
    initialState,
    title,
    titleClassName,
    enableSearch = false,
    searchColumn = '',
    enablePagination = false,
    enableSorting = false,
    paginationState,
    onPaginationChange,
    sortingState,
    onSortingChange,
    enableSortingRemoval = true,
    className,
    cellClassName,
    extra,
    hasActionColumn,
    hideHeader = false,
    rowId,
    enableExport,
    exportOptions,
    wrapperClassName,
    defaultColumn,
    isLoading,
    searchQuery,
    testId,
  }: DataTableProps<TData, TValue>,
  ref: ForwardedRef<HTMLTableElement>,
) => {
  const [sorting, setSorting] = useState<SortingState>(sortingState || [])
  const [pagination, setPagination] = useState<PaginationState>(
    paginationState || {
      pageIndex: 0,
      pageSize: 10,
    },
  )
  const [userHasSorted, setUserHasSorted] = useState(
    (sortingState?.length ?? 0) === 0,
  )

  // @ts-ignore
  const table = useReactTable({
    data,
    columns,
    initialState,
    enableSortingRemoval,
    getCoreRowModel: getCoreRowModel(),
    ...(defaultColumn && { defaultColumn }),
    ...(enableSearch && { getFilteredRowModel: getFilteredRowModel() }),
    ...(enablePagination && {
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: onPaginationChange || setPagination,
    }),
    ...(enableSorting && {
      getSortedRowModel: getSortedRowModel(),
      onSortingChange: onSortingChange || setSorting,
    }),
    state: {
      ...(enablePagination && { pagination }),
      ...(enableSorting && { sorting }),
    },
  })

  useEffect(() => {
    if (enableSearch) {
      if (searchColumn) {
        table.getColumn(searchColumn)?.setFilterValue(searchQuery)
      } else {
        console.warn('No search column provided to DataTable')
      }
    }
  }, [enableSearch, searchColumn, searchQuery, table])

  const handleHeaderClick = useCallback(
    <TData, TValue>(header: Header<TData, TValue>) =>
      () => {
        if (enableSorting) {
          // Toggle sorting only if not in initial state or already sorted
          if (userHasSorted) {
            header.column.toggleSorting()
          } else {
            // Apply the initial sorting state
            setSorting([{ id: header.column.id, desc: false }])
            setUserHasSorted(true)
          }
        }
      },
    [enableSorting, userHasSorted],
  )

  const renderLoadingSkeleton = useMemo(() => {
    return Array.from({ length: 10 }).map(() =>
      table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id}>
          {headerGroup.headers.map((header) => {
            const meta = header.column.columnDef.meta
            const isActionColumn = meta?.isActionColumn

            return (
              <TableCell key={header.id}>
                {isActionColumn ? (
                  <div className="flex items-center justify-end gap-2">
                    <Skeleton className="h-4 w-4 dark:bg-slate-500" />
                    <Skeleton className="h-4 w-4 dark:bg-slate-500" />
                  </div>
                ) : meta?.skeleton?.customCell ? (
                  meta.skeleton.customCell()
                ) : (
                  <Skeleton
                    className="h-4 dark:bg-slate-500"
                    style={{
                      width: `${Math.floor(Math.random() * 81 + 20)}%`,
                    }}
                  />
                )}
              </TableCell>
            )
          })}
        </TableRow>
      )),
    )
  }, [table])

  const headerGroups = table.getHeaderGroups()

  // Renders the table header based on the header groups and headers of the table.
  const renderHeader = useMemo(() => {
    return headerGroups.map((headerGroup) => (
      <TableRow key={headerGroup.id}>
        {headerGroup.headers.map((header, index) => (
          <TableHead
            key={header.id}
            onClick={handleHeaderClick(header)}
            className={classNames({
              'w-full': header.id.endsWith('-full'),
              'cursor-pointer select-none':
                header.column.getCanSort() && enableSorting,
              'pr-6 text-right': hasActionColumn && header.id === 'actions',
            })}
          >
            <div
              className={classNames('flex items-center', {
                'justify-end': index === headerGroup.headers.length - 1,
              })}
            >
              {flexRender(header.column.columnDef.header, header.getContext())}
              {header.column.getCanSort() && enableSorting && (
                <div className="text-secondary-500 dark:text-secondary-100 ml-2 flex flex-col items-center">
                  <ChevronBottomIcon
                    className={classNames(
                      'h-3 rotate-180',
                      header.column.getIsSorted() === 'asc' &&
                        userHasSorted &&
                        'text-primary-500 dark:text-primary-500',
                    )}
                  />
                  <ChevronBottomIcon
                    className={classNames(
                      'mt-[-5px] h-3',
                      header.column.getIsSorted() === 'desc' &&
                        userHasSorted &&
                        'text-primary-500 dark:text-primary-500',
                    )}
                  />
                </div>
              )}
            </div>
          </TableHead>
        ))}
      </TableRow>
    ))

    // work-around to render table again to show active sorted icon in primary
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    table,
    headerGroups,
    handleHeaderClick,
    enableSorting,
    hasActionColumn,
    sorting,
  ])

  // Renders the table rows based on the row model of the table.
  // If there are no rows in the model, it renders a single row indicating no results.
  const renderRows = useMemo(
    () => {
      if (table.getRowModel().rows?.length === 0) {
        return (
          <TableRow>
            <TableCell
              colSpan={columns.length}
              className={classNames('h-24 text-center', cellClassName)}
            >
              <EmptySlot>
                {() => <EmptyTableContent>No results.</EmptyTableContent>}
              </EmptySlot>
            </TableCell>
          </TableRow>
        )
      }

      return table.getRowModel().rows.map((row) => (
        <TableRow
          key={row.id}
          data-state={row.getIsSelected() && 'selected'}
          data-id={rowId?.(row.original)}
          className={classNames({
            'w-full': row.id.endsWith('-full'),
          })}
        >
          {row.getVisibleCells().map((cell) => {
            return (
              <TableCell
                key={cell.id}
                width={cell.column.columnDef.size}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            )
          })}
        </TableRow>
      ))
    },
    // data - added as workaround, otherwise it not renders items properly
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cellClassName, columns, rowId, sorting, table, pagination, data],
  )

  return (
    <div
      className={classNames(
        'flex w-full flex-col overflow-auto',
        wrapperClassName,
      )}
      data-testid={testId}
    >
      {(title || enableSearch || enableExport || extra) && (
        <div className="flex flex-col justify-between gap-4 pb-4 md:flex-row">
          {title && (
            <h2
              className={classNames(
                'text-secondary-500 text-2xl font-semibold dark:text-white',
                titleClassName,
              )}
              data-testid={`${testId}-title`}
            >
              {title}
            </h2>
          )}
          {/* Check if searchQuery is undefined to render the search input, since a defined searchQuery means
              the parent component is controlling the search state externally */}
          {((enableSearch && searchQuery === undefined) ||
            enableExport ||
            extra) && (
            <div className="flex flex-col items-center gap-2 sm:flex-row md:ml-auto">
              <div className="flex gap-2">
                {enableExport && exportOptions && (
                  <Export
                    {...exportOptions}
                    disabled={!data.length}
                  />
                )}
                {enableSearch && (
                  <SearchInput
                    placeholder="Search"
                    testId={`${testId}-search-input`}
                    onInputChange={(value) =>
                      table.getColumn(searchColumn)?.setFilterValue(value)
                    }
                  />
                )}
              </div>
              {extra}
            </div>
          )}
        </div>
      )}
      <div className={classNames('bg-card rounded-2xl p-4', className)}>
        <Table
          ref={ref}
          data-testid={`${testId}-table`}
        >
          {!hideHeader && <TableHeader>{renderHeader}</TableHeader>}
          <TableBody>
            {isLoading ? renderLoadingSkeleton : renderRows}
          </TableBody>
        </Table>
      </div>
      {enablePagination && (
        <DataTablePagination
          table={table}
          allRowsLength={data.length}
          pagination={pagination}
        />
      )}
    </div>
  )
}

export const DataTable = forwardRef(DataTableInner) as <TData, TValue>(
  props: DataTableProps<TData, TValue> & {
    ref?: ForwardedRef<HTMLTableElement>
  },
) => ReturnType<typeof DataTableInner>
